import { Button } from "@/components/ui/button";
import {
  Share2, AlertTriangle, CheckCircle, TrendingUp,
  Zap, Play, ArrowRight, Loader2, Sparkles,
  ShieldCheck, Eye, BadgeCheck, Search, Flame, FileText,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useDashboardOverview, type DashboardOverview } from "@/hooks/use-dashboard";
import { getBenchmark } from "@/lib/benchmarks";

// ─── Shared card wrapper ────────────────────────────────────

function DashCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`p-6 bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border/10 ${className}`}>
      {children}
    </div>
  );
}

// ─── Hero Card ───────────────────────────────────────────────

function HeroEmpty({ year, lastYear, assessmentId }: { year: number; lastYear?: number; assessmentId: string | null }) {
  const [, setLocation] = useLocation();
  return (
    <DashCard className="p-8 relative overflow-hidden">
      <div className="absolute top-2 right-6 opacity-5">
        <Sparkles className="h-32 w-32 text-primary" />
      </div>
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-muted-foreground">{"New Assessment Cycle"}</p>
          <h2 className="text-3xl font-bold text-foreground">{`Start Your FY ${year} Assessment`}</h2>
          {lastYear && (
            <p className="text-sm text-muted-foreground pt-1">
              {`Last report: FY ${lastYear} (Locked)`}
            </p>
          )}
        </div>
        <Button
          size="lg"
          className="gap-2 rounded-2xl px-6 py-3 text-base font-bold"
          onClick={() => setLocation(assessmentId ? `/workflow/${assessmentId}` : "/assessments")}
        >
          <Play className="h-4 w-4" /> {"Begin Assessment"}
        </Button>
      </div>
    </DashCard>
  );
}

function CircularRing({ percent }: { percent: number }) {
  const radius = 40;
  const stroke = 6;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg width={radius * 2} height={radius * 2} className="shrink-0 -rotate-90">
      {/* Track */}
      <circle
        cx={radius}
        cy={radius}
        r={normalizedRadius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        className="text-muted/30"
      />
      {/* Progress */}
      <circle
        cx={radius}
        cy={radius}
        r={normalizedRadius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-primary transition-all duration-500"
      />
    </svg>
  );
}

function HeroInProgress({
  completeness,
  resumeStep,
  orgName,
  year,
  assessmentId,
}: {
  completeness: number;
  resumeStep: { tab: string; label: string } | null;
  orgName: string | null;
  year: number;
  assessmentId: string | null;
}) {
  const [, setLocation] = useLocation();
  const baseUrl = assessmentId ? `/workflow/${assessmentId}` : "/assessments";
  const resumeUrl = resumeStep ? `${baseUrl}?tab=${resumeStep.tab}` : baseUrl;

  return (
    <DashCard className="p-8 relative overflow-hidden">
      <div className="absolute top-2 right-6 opacity-5">
        <Zap className="h-32 w-32 text-primary" />
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-6">
          <CircularRing percent={completeness} />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-muted-foreground">
              {`${orgName || "Assessment"} · FY ${year}`}
            </p>
            <h2 className="text-5xl font-bold text-foreground leading-[48px]">
              {`${completeness}% Complete`}
            </h2>
            <div className="w-full max-w-md pt-3">
              <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${completeness}%` }}
                />
              </div>
            </div>
            {resumeStep && (
              <p className="text-sm text-muted-foreground pt-1">{resumeStep.label}</p>
            )}
          </div>
        </div>
        <Button
          size="lg"
          className="gap-2 rounded-2xl px-6 py-3 text-base font-bold"
          onClick={() => setLocation(resumeUrl)}
        >
          <ArrowRight className="h-4 w-4" /> {"Resume Assessment"}
        </Button>
      </div>
    </DashCard>
  );
}

function HeroCompleted({
  totalEmissions,
  vsmeLevel,
  shareLink,
  year,
}: {
  totalEmissions: number | null;
  vsmeLevel: string | null;
  shareLink: { token: string; viewCount: number } | null;
  year: number;
}) {
  const [, setLocation] = useLocation();
  return (
    <DashCard className="p-8 relative overflow-hidden">
      <div className="absolute top-2 right-6 opacity-5 pointer-events-none">
        <CheckCircle className="h-32 w-32 text-primary" />
      </div>
      <div className="relative z-10 flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-muted-foreground">
            {`FY ${year} Assessment — Finalized`}
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-bold text-foreground leading-[48px]">
              {totalEmissions != null ? totalEmissions.toFixed(3) : "Completed"}
            </span>
            {totalEmissions != null && (
              <span className="text-2xl font-medium text-muted-foreground">tCO2e</span>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            {vsmeLevel && (
              <span className="inline-flex items-center gap-1.5 text-base font-semibold text-muted-foreground">
                <BadgeCheck className="h-[18px] w-[18px] text-primary" />
                {vsmeLevel}
              </span>
            )}
            {shareLink && (
              <span className="inline-flex items-center gap-1 text-sm text-green-700 font-medium">
                <Share2 className="h-3 w-3" /> {`Link Active · ${shareLink.viewCount} views`}
              </span>
            )}
          </div>
        </div>
        <Button
          size="lg"
          className="gap-2 rounded-2xl px-6 py-3 text-base font-bold"
          onClick={() => setLocation("/report")}
        >
          {"View Full Report"}
        </Button>
      </div>
    </DashCard>
  );
}

function HeroSection({ data }: { data: DashboardOverview }) {
  const ca = data.currentAssessment;
  const lastLocked = data.assessmentHistory.find(
    (a) => a.locked && a.reportingYear !== ca.reportingYear
  );

  if (ca.status === "none") {
    return <HeroEmpty year={ca.reportingYear} lastYear={lastLocked?.reportingYear} assessmentId={ca.id} />;
  }
  if (ca.status === "in_progress") {
    return (
      <HeroInProgress
        completeness={ca.completenessPercent}
        resumeStep={ca.resumeStep}
        orgName={ca.organizationName}
        year={ca.reportingYear}
        assessmentId={ca.id}
      />
    );
  }
  return (
    <HeroCompleted
      totalEmissions={ca.totalEmissionsTco2e}
      vsmeLevel={ca.vsmeLevel}
      shareLink={ca.shareLink}
      year={ca.reportingYear}
    />
  );
}

// ─── Shared icon circle ─────────────────────────────────────

function IconCircle({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center shrink-0">
      {children}
    </div>
  );
}

// ─── Action Center ──────────────────────────────────────────

function ActionCenter({
  alerts,
  assessmentId,
}: {
  alerts: DashboardOverview["dataAlerts"];
  assessmentId: string | null;
}) {
  const baseUrl = assessmentId ? `/workflow/${assessmentId}` : "/assessments";
  const visible = alerts.slice(0, 5);
  const overflow = alerts.length - visible.length;

  return (
    <DashCard>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
          {alerts.length === 0 ? (
            <CheckCircle className="h-5 w-5 text-accent" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-amber-500" />
          )}
        </div>
        <h3 className="text-xl font-bold text-foreground">
          {"What You Need to Do Next"}
        </h3>
      </div>

      {alerts.length === 0 ? (
        <p className="text-base text-muted-foreground">
          {"No actions needed. Your data looks complete."}
        </p>
      ) : (
        <div className="space-y-3">
          {visible.map((alert, i) => {
            const isWarning = alert.severity === "warning";
            return (
              <div
                key={i}
                className={`flex items-start gap-3 p-4 rounded-2xl border ${
                  isWarning
                    ? "bg-red-50 border-red-100"
                    : "bg-amber-50 border-amber-100"
                }`}
              >
                <span
                  className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${
                    isWarning
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {isWarning
                    ? "High Priority"
                    : "Medium Priority"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isWarning ? "text-red-900" : "text-amber-900"}`}>
                    {alert.message}
                  </p>
                  <Link
                    href={`${baseUrl}?tab=${alert.workflowTab}`}
                    className="text-xs underline font-medium mt-1 inline-block hover:opacity-80"
                  >
                    {"Fix in Workflow"} →
                  </Link>
                </div>
              </div>
            );
          })}
          {overflow > 0 && (
            <p className="text-sm text-muted-foreground pl-1">
              {`and ${overflow} more…`}
            </p>
          )}
        </div>
      )}
    </DashCard>
  );
}

// ─── Simulator CTA ───────────────────────────────────────────

function SimulatorCTA() {
  const [, setLocation] = useLocation();
  return (
    <DashCard className="flex-1 p-8 bg-primary/5 relative overflow-hidden flex flex-col justify-between shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)]">
      {/* Decorative blur circle */}
      <div className="w-32 h-32 absolute -top-12 -right-8 bg-primary/10 rounded-full blur-[32px]" />

      <div>
        <div className="pb-6">
          <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
            <Search className="h-5 w-5 text-primary" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-foreground">{"Reduction Simulator"}</h3>
        <p className="text-sm font-medium text-muted-foreground pt-1">
          {"Plan your path to net zero"}
        </p>
        <p className="text-base text-muted-foreground pt-3">
          {"What if you switched to solar? Test reduction targets and see projected savings before committing."}
        </p>
      </div>

      <div className="pt-8">
        <Button
          className="w-full gap-2 rounded-3xl px-6 py-3 text-sm font-bold"
          onClick={() => setLocation("/simulator")}
        >
          {"Open Simulator"}
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </DashCard>
  );
}

// ─── Report History Table ────────────────────────────────────

function ReportHistory({ history }: { history: DashboardOverview["assessmentHistory"] }) {
  const [, setLocation] = useLocation();

  if (history.length === 0) {
    return null;
  }

  const statusLabels: Record<string, string> = {
    draft: "Draft",
    complete: "Complete",
    finalized: "Finalized",
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: "bg-gray-100 text-gray-700",
      complete: "bg-blue-100 text-blue-700",
      finalized: "bg-green-100 text-green-700",
    };
    return (
      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${styles[status] || styles.draft}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  return (
    <DashCard className="p-0 overflow-hidden">
      {/* Header */}
      <div className="px-8 pt-6 pb-4 border-b border-border/10">
        <h3 className="text-xl font-bold text-foreground">{"Reporting History"}</h3>
        <p className="text-base text-muted-foreground">{"Track and manage your organization's sustainability reports."}</p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30">
              <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {"Reporting Period"}
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {"Last Updated"}
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {"Trend"}
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {"Readiness level"}
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {"Status"}
              </th>
              <th className="px-6 py-3 text-right text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {"Actions"}
              </th>
            </tr>
          </thead>
          <tbody>
            {history.map((row, idx) => (
              <tr key={row.id} className="border-t border-border/5">
                <td className="px-6 py-5 font-semibold text-foreground">
                  {`FY ${row.reportingYear}`}
                </td>
                <td className="px-6 py-5 text-muted-foreground">
                  {row.updatedAt
                    ? new Date(row.updatedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "\u2014"}
                </td>
                <td className="px-6 py-5">
                  {(() => {
                    const older = history[idx + 1];
                    if (!older || !row.totalEmissionsTco2e || !older.totalEmissionsTco2e) {
                      return <span className="text-muted-foreground">—</span>;
                    }
                    return row.totalEmissionsTco2e < older.totalEmissionsTco2e ? (
                      <span className="text-green-600 font-bold">↓</span>
                    ) : (
                      <span className="text-red-500 font-bold">↑</span>
                    );
                  })()}
                </td>
                <td className="px-6 py-5 text-muted-foreground">{row.vsmeLevel}</td>
                <td className="px-6 py-5">{statusBadge(row.status)}</td>
                <td className="px-6 py-5 text-right">
                  {row.locked ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 cursor-pointer"
                      onClick={() => setLocation(`/report/${row.id}`)}
                    >
                      <Eye className="h-4 w-4 text-primary" />
                      {"View Report"}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 cursor-pointer"
                      onClick={() => setLocation(`/workflow/${row.id}`)}
                    >
                      <ArrowRight className="h-4 w-4 text-primary" />
                      {"Continue"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashCard>
  );
}

// ─── Emissions Overview ──────────────────────────────────────

function EmissionsOverview({ data }: { data: DashboardOverview }) {
  const ca = data.currentAssessment;
  const scope1 = ca.scope1Tco2e ?? 0;
  const scope2 = ca.scope2Tco2e ?? 0;
  const scope3 = ca.scope3Tco2e ?? 0;
  const total = scope1 + scope2 + scope3;

  if (total <= 0) return null;

  const pct = (val: number) => (total > 0 ? (val / total) * 100 : 0);

  const priorYear = data.assessmentHistory.find(
    (a) => a.reportingYear < ca.reportingYear
  );
  const showYoyDrop =
    priorYear &&
    priorYear.totalEmissionsTco2e > 0 &&
    ca.totalEmissionsTco2e != null &&
    ca.totalEmissionsTco2e < priorYear.totalEmissionsTco2e;

  const bars = [
    { label: "Scope 1 – Fuel combustion", value: scope1, color: "bg-blue-500" },
    { label: "Scope 2 – Electricity", value: scope2, color: "bg-amber-500" },
    { label: "Scope 3 – Travel & commuting", value: scope3, color: "bg-emerald-500" },
  ];

  return (
    <DashCard>
      <h3 className="text-xl font-bold text-foreground mb-5">
        {`Your Carbon Footprint – FY ${ca.reportingYear}`}
      </h3>
      <div className="space-y-4">
        {bars.map(({ label, value, color }) => (
          <div key={label}>
            <div className="flex justify-between text-sm mb-1.5">
              <span className="text-muted-foreground font-medium">{label}</span>
              <span className="font-semibold text-foreground">
                {value.toFixed(3)} tCO₂e
              </span>
            </div>
            <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${color}`}
                style={{ width: `${pct(value)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      {showYoyDrop && (
        <div className="mt-5 flex items-center gap-2 px-4 py-3 bg-green-50 rounded-2xl border border-green-100">
          <TrendingUp className="h-4 w-4 text-green-600 shrink-0" />
          <p className="text-sm font-medium text-green-800">
            {`↓ Your emissions dropped vs FY ${priorYear!.reportingYear}. Great progress.`}
          </p>
        </div>
      )}
    </DashCard>
  );
}

// ─── Quick Links ─────────────────────────────────────────────

function QuickLinks({ data }: { data: DashboardOverview }) {
  const [, setLocation] = useLocation();
  const ca = data.currentAssessment;

  const prevLocked = data.assessmentHistory.find(
    (a) => a.locked && a.reportingYear < ca.reportingYear
  );

  const links = [
    {
      label: "Continue Assessment",
      icon: <ArrowRight className="h-4 w-4" />,
      onClick: () => setLocation(ca.id ? `/workflow/${ca.id}` : "/assessments"),
      disabled: ca.status === "none" || ca.status === "completed",
    },
    {
      label: "Generate Report",
      icon: <Eye className="h-4 w-4" />,
      onClick: () => setLocation("/report"),
      disabled: ca.status === "none",
    },
    {
      label: "View Previous Report",
      icon: <FileText className="h-4 w-4" />,
      onClick: () => prevLocked && setLocation(`/report/${prevLocked.id}`),
      disabled: !prevLocked,
    },
    {
      label: "Share Report",
      icon: <Share2 className="h-4 w-4" />,
      onClick: () => setLocation("/report"),
      disabled: ca.status !== "completed",
    },
  ];

  return (
    <DashCard className="flex-1 flex flex-col justify-between">
      <h3 className="text-xl font-bold text-foreground mb-5">
        {"Quick Actions"}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {links.map(({ label, icon, onClick, disabled }) => (
          <Button
            key={label}
            variant="outline"
            className={`gap-2 rounded-2xl justify-start text-sm h-auto py-3 px-4 ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
          >
            {icon}
            <span className="truncate">{label}</span>
          </Button>
        ))}
      </div>
    </DashCard>
  );
}

// ─── KPI Cards ───────────────────────────────────────────────

function KpiCard({
  icon,
  title,
  value,
  unit,
  secondary,
  secondaryColor = "text-muted-foreground",
  children,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  unit?: string;
  secondary?: string;
  secondaryColor?: string;
  children?: React.ReactNode;
}) {
  return (
    <DashCard className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <IconCircle>{icon}</IconCircle>
        <span className="text-sm font-semibold text-muted-foreground">{title}</span>
      </div>
      <div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-bold text-foreground">{value}</span>
          {unit && <span className="text-sm text-muted-foreground font-medium">{unit}</span>}
        </div>
        {secondary && (
          <p className={`text-sm mt-1 font-medium ${secondaryColor}`}>{secondary}</p>
        )}
        {children}
      </div>
    </DashCard>
  );
}

const VSME_LEVEL_LABELS: Record<string, string> = {
  "Not Ready": "Not Ready",
  "In Progress": "In Progress",
  "Approaching Level 1": "Approaching Level 1",
  "Level 1 Ready": "Level 1 Ready",
};

function KpiCards({ data }: { data: DashboardOverview }) {
  const ca = data.currentAssessment;

  // Card 1 — Total Emissions + YoY delta
  const priorYear = data.assessmentHistory.find(
    (a) => a.reportingYear < ca.reportingYear
  );
  let yoyText: string | undefined;
  let yoyColor = "text-muted-foreground";
  if (priorYear && ca.totalEmissionsTco2e != null && priorYear.totalEmissionsTco2e > 0) {
    const delta = ((ca.totalEmissionsTco2e - priorYear.totalEmissionsTco2e) / priorYear.totalEmissionsTco2e) * 100;
    const sign = delta <= 0 ? "−" : "+";
    yoyText = `${sign}${Math.abs(delta).toFixed(1)}% vs FY ${priorYear.reportingYear}`;
    yoyColor = delta <= 0 ? "text-green-600" : "text-red-500";
  }

  // Card 2 — Emission Intensity
  const totalEmployees = ca.scopeSummary?.operationalScale.totalEmployees ?? 0;
  const benchmark = ca.sector ? getBenchmark(ca.sector) : null;
  const intensity =
    ca.totalEmissionsTco2e != null && totalEmployees > 0
      ? ca.totalEmissionsTco2e / totalEmployees
      : null;
  let intensitySecondary: string | undefined;
  let intensityColor = "text-muted-foreground";
  if (intensity != null && benchmark?.employeeIntensity != null) {
    if (intensity <= benchmark.employeeIntensity) {
      intensitySecondary = "Below sector avg";
      intensityColor = "text-green-600";
    } else {
      intensitySecondary = "Above sector avg";
      intensityColor = "text-red-500";
    }
  }

  // Card 3 — VSME Maturity
  const vsmeLevelLabel = VSME_LEVEL_LABELS[ca.vsmeLevel ?? ""] ?? "Not Ready";

  // Card 4 — Data Completeness
  const completeness = ca.completenessPercent;
  const completenessColor =
    completeness >= 85 ? "text-green-600" : completeness >= 50 ? "text-amber-500" : "text-red-500";
  const completenessBarColor =
    completeness >= 85 ? "bg-green-500" : completeness >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
      {/* Card 1 */}
      <KpiCard
        icon={<Flame className="h-5 w-5 text-primary" />}
        title={"Total Emissions"}
        value={ca.totalEmissionsTco2e != null ? ca.totalEmissionsTco2e.toFixed(3) : "—"}
        unit="tCO₂e"
        secondary={yoyText}
        secondaryColor={yoyColor}
      />

      {/* Card 2 */}
      <KpiCard
        icon={<Zap className="h-5 w-5 text-primary" />}
        title={"Emission Intensity"}
        value={intensity != null ? intensity.toFixed(3) : "—"}
        unit={intensity != null ? "tCO₂e / employee" : undefined}
        secondary={
          intensity == null
            ? "Add workforce data to unlock"
            : intensitySecondary
        }
        secondaryColor={intensity == null ? "text-muted-foreground italic text-xs" : intensityColor}
      />

      {/* Card 3 */}
      <KpiCard
        icon={<BadgeCheck className="h-5 w-5 text-primary" />}
        title={"VSME Maturity Level"}
        value={vsmeLevelLabel}
        secondary={`${completeness}% toward Level 1`}
        secondaryColor="text-primary font-semibold"
      >
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${completeness}%` }}
          />
        </div>
      </KpiCard>

      {/* Card 4 */}
      <KpiCard
        icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        title={"Data Completeness"}
        value={`${completeness}%`}
        secondary={`${ca.scopeSummary?.evidenceStats.totalDocuments ?? 0} documents uploaded`}
        secondaryColor={completenessColor}
      >
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-2">
          <div
            className={`h-full rounded-full transition-all duration-500 ${completenessBarColor}`}
            style={{ width: `${completeness}%` }}
          />
        </div>
      </KpiCard>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────

export default function Dashboard() {
  const { data, isLoading, error } = useDashboardOverview();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">{"Dashboard"}</h1>
          <p className="text-muted-foreground mt-1">{"Unable to load dashboard data."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Hero */}
      <HeroSection data={data} />

      {/* KPI Cards */}
      {data.currentAssessment.status !== "none" && (
        <KpiCards data={data} />
      )}

      {/* Action Center */}
      {data.currentAssessment.status !== "none" && (
        <ActionCenter
          alerts={data.dataAlerts}
          assessmentId={data.currentAssessment.id}
        />
      )}

      {/* Emissions Overview */}
      <EmissionsOverview data={data} />

      {/* Simulator + Quick Links */}
      <div className="flex gap-8">
        <SimulatorCTA />
        <QuickLinks data={data} />
      </div>

      {/* Report Archive */}
      {data.assessmentHistory.length > 0 && (
        <ReportHistory history={data.assessmentHistory} />
      )}
    </div>
  );
}
